import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ChromeState = {
  hideNavbar: boolean;
  hideSidebar: boolean;
  hideFooter: boolean;
};

const DEFAULT_STATE: ChromeState = {
  hideNavbar: false,
  hideSidebar: false,
  hideFooter: false,
};

type ChromeContextValue = {
  chrome: ChromeState;
  setChrome: (next: ChromeState) => void;
};

const ChromeContext = createContext<ChromeContextValue | null>(null);

export const ChromeProvider = ({ children }: { children: ReactNode }) => {
  const [chrome, setChromeState] = useState<ChromeState>(DEFAULT_STATE);

  const setChrome = useCallback((next: ChromeState) => {
    setChromeState((prev) =>
      prev.hideNavbar === next.hideNavbar &&
      prev.hideSidebar === next.hideSidebar &&
      prev.hideFooter === next.hideFooter
        ? prev
        : next
    );
  }, []);

  const value = useMemo(() => ({ chrome, setChrome }), [chrome, setChrome]);

  return (
    <ChromeContext.Provider value={value}>{children}</ChromeContext.Provider>
  );
};

export const useChromeState = (): ChromeState => {
  const ctx = useContext(ChromeContext);
  return ctx?.chrome ?? DEFAULT_STATE;
};

export const useChromeOverride = (next: ChromeState) => {
  const ctx = useContext(ChromeContext);
  const setChrome = ctx?.setChrome;
  useLayoutEffect(() => {
    if (!setChrome) return;
    setChrome(next);
  }, [setChrome, next.hideNavbar, next.hideSidebar, next.hideFooter]);
};
